import { useState, useEffect, useCallback, RefObject, useRef } from 'react';
import { usePreferences, UIPreferences } from './usePreferences';
import { useIsMobile } from './useIsMobile';

interface Position {
  x: number;
  y: number;
}

interface UseDraggableOptions {
  defaultPosition: Position;
  mobileDefaultPosition?: Position; // Default position for mobile (optional)
  preferenceKey?: keyof UIPreferences; // e.g., 'tradingPanel', 'predictionPanel'
  constrainToViewport?: boolean;
  excludeSelectors?: string[]; // CSS selectors to exclude from drag (e.g., ['button', 'input', '.resize-handle'])
}

interface UseDraggableReturn {
  position: Position;
  isDragging: boolean;
  dragHandlers: {
    onMouseDown: (e: React.MouseEvent) => void;
    onTouchStart?: (e: React.TouchEvent) => void;
  };
  setPosition: (pos: Position) => void;
}

/**
 * A reusable hook for making elements draggable with position persistence.
 *
 * @param elementRef - Ref to the draggable element (used for viewport constraints)
 * @param options - Configuration options
 * @returns Object with position state, drag handlers, and utilities
 *
 * @example
 * ```tsx
 * const MyPanel = () => {
 *   const panelRef = useRef<HTMLDivElement>(null);
 *   const { position, isDragging, dragHandlers } = useDraggable(panelRef, {
 *     defaultPosition: { x: 100, y: 100 },
 *     preferenceKey: 'myPanel',
 *     constrainToViewport: true,
 *     excludeSelectors: ['button', 'input']
 *   });
 *
 *   return (
 *     <div
 *       ref={panelRef}
 *       style={{ left: position.x, top: position.y }}
 *       {...dragHandlers}
 *     >
 *       Drag me!
 *     </div>
 *   );
 * };
 * ```
 */
export function useDraggable(
  elementRef: RefObject<HTMLElement>,
  options: UseDraggableOptions
): UseDraggableReturn {
  const { preferences, updatePreference, isLoading } = usePreferences();
  const isMobile = useIsMobile();
  const hasSyncedFromServer = useRef(false);

  // Get the appropriate default position based on device
  const getDefaultPosition = (): Position => {
    if (isMobile && options.mobileDefaultPosition) {
      return options.mobileDefaultPosition;
    }
    return options.defaultPosition;
  };

  // Get saved position from preferences
  const getSavedPosition = useCallback((): Position | undefined => {
    if (!options.preferenceKey) return undefined;
    const pref = preferences[options.preferenceKey];
    if (pref && typeof pref === 'object') {
      // Check for mobile-specific position first when on mobile
      if (isMobile && 'mobilePosition' in pref && pref.mobilePosition) {
        return pref.mobilePosition as Position;
      }
      // Fall back to desktop position
      if ('position' in pref && pref.position) {
        return pref.position as Position;
      }
    }
    return undefined;
  }, [options.preferenceKey, preferences, isMobile]);

  // Clamp a position so the element stays fully within the viewport.
  // Mirrors the in-drag constraint, but also applied on load/resize so a
  // coordinate saved on a larger window (or before the element grew) can't
  // strand the element off-screen. No-op unless constrainToViewport is set.
  const clampToViewport = useCallback((pos: Position): Position => {
    if (!options.constrainToViewport || !elementRef.current) return pos;
    const rect = elementRef.current.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return pos; // not laid out yet
    const maxX = Math.max(0, window.innerWidth - rect.width);
    const maxY = Math.max(0, window.innerHeight - rect.height);
    return {
      x: Math.min(Math.max(pos.x, 0), maxX),
      y: Math.min(Math.max(pos.y, 0), maxY),
    };
  }, [options.constrainToViewport, elementRef]);

  const [position, setPosition] = useState<Position>(getDefaultPosition());
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState<Position>({ x: 0, y: 0 });

  // Sync position FROM server preferences when they load (one-time)
  // We need to wait until preferences are actually populated, not just when isLoading becomes false
  useEffect(() => {
    if (hasSyncedFromServer.current || !options.preferenceKey) return;

    const savedPos = getSavedPosition();
    console.log(`[useDraggable] ${options.preferenceKey} check: isLoading=${isLoading}, savedPos=`, savedPos);

    if (savedPos) {
      console.log(`[useDraggable] Syncing ${options.preferenceKey} position from server:`, savedPos);
      setPosition(savedPos);
      hasSyncedFromServer.current = true;
      // Re-fit after layout: a coord saved on a bigger window (or before the
      // element grew) may now sit off-screen. Heals the stored value via the save effect.
      requestAnimationFrame(() => setPosition((prev) => clampToViewport(prev)));
    } else if (!isLoading) {
      // Only mark as synced if loading is complete but no position found
      // This means server has no saved position, so use default
      console.log(`[useDraggable] ${options.preferenceKey} - loading complete, no saved position, using default`);
      hasSyncedFromServer.current = true;
    }
    // If isLoading is true and savedPos is undefined, we're still waiting for preferences
  }, [isLoading, options.preferenceKey, getSavedPosition, clampToViewport]);

  // Keep the element on-screen when the window is resized (e.g. moved to a
  // smaller monitor). No-op unless constrainToViewport is set.
  useEffect(() => {
    if (!options.constrainToViewport) return;
    const onResize = () => setPosition((prev) => clampToViewport(prev));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [options.constrainToViewport, clampToViewport]);

  // Save position to preferences when it changes (only after initial sync)
  // Saves to mobilePosition for mobile, position for desktop
  useEffect(() => {
    if (options.preferenceKey && !isDragging && hasSyncedFromServer.current) {
      if (isMobile) {
        updatePreference(options.preferenceKey, { mobilePosition: position } as any);
      } else {
        updatePreference(options.preferenceKey, { position } as any);
      }
    }
  }, [position, options.preferenceKey, updatePreference, isDragging, isMobile]);

  /**
   * Check if the target element should be excluded from dragging
   */
  const shouldExclude = useCallback((target: HTMLElement): boolean => {
    if (!options.excludeSelectors?.length) return false;

    for (const selector of options.excludeSelectors) {
      // Check if target matches selector or has a parent that matches
      if (target.matches(selector) || target.closest(selector)) {
        return true;
      }
    }
    return false;
  }, [options.excludeSelectors]);

  /**
   * Mouse down handler - initiates dragging
   */
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    const target = e.target as HTMLElement;

    // Check for excluded elements
    if (shouldExclude(target)) return;

    e.preventDefault();
    e.stopPropagation();

    if (elementRef.current) {
      const rect = elementRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
      setIsDragging(true);
    }
  }, [elementRef, shouldExclude]);

  /**
   * Touch start handler - initiates dragging on mobile
   */
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const target = e.target as HTMLElement;

    // Check for excluded elements
    if (shouldExclude(target)) return;

    const touch = e.touches[0];
    if (elementRef.current) {
      const rect = elementRef.current.getBoundingClientRect();
      setDragOffset({
        x: touch.clientX - rect.left,
        y: touch.clientY - rect.top,
      });
      setIsDragging(true);
    }
  }, [elementRef, shouldExclude]);

  // Handle mouse/touch move and up events
  useEffect(() => {
    if (!isDragging) return;

    /**
     * Update position during drag
     */
    const handleMouseMove = (e: MouseEvent) => {
      let newX = e.clientX - dragOffset.x;
      let newY = e.clientY - dragOffset.y;

      if (options.constrainToViewport && elementRef.current) {
        const rect = elementRef.current.getBoundingClientRect();
        newX = Math.max(0, Math.min(newX, window.innerWidth - rect.width));
        newY = Math.max(0, Math.min(newY, window.innerHeight - rect.height));
      }

      setPosition({ x: newX, y: newY });
    };

    const handleTouchMove = (e: TouchEvent) => {
      const touch = e.touches[0];
      let newX = touch.clientX - dragOffset.x;
      let newY = touch.clientY - dragOffset.y;

      if (options.constrainToViewport && elementRef.current) {
        const rect = elementRef.current.getBoundingClientRect();
        newX = Math.max(0, Math.min(newX, window.innerWidth - rect.width));
        newY = Math.max(0, Math.min(newY, window.innerHeight - rect.height));
      }

      setPosition({ x: newX, y: newY });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    const handleTouchEnd = () => {
      setIsDragging(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('touchmove', handleTouchMove);
    document.addEventListener('touchend', handleTouchEnd);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging, dragOffset, options.constrainToViewport, elementRef]);

  return {
    position,
    isDragging,
    dragHandlers: {
      onMouseDown: handleMouseDown,
      onTouchStart: handleTouchStart,
    },
    setPosition,
  };
}
